/* Shared UI primitives - buttons, modals, forms, dropdowns */

/* ---- Accessibility ---- */
.ariaDisabled {
  opacity: 0.5;
  pointer-events: none;
}

/* ---- Buttons ----
   ONE SHAPE (v8.424.0): the main choice is filled, the others are
   outlined. No button carries its own colour; the livery's accent does. */
.btnPrimary {
  background: var(--accent);
  color: var(--bg-base);
  border: 1px solid var(--accent);
  border-radius: var(--radius-sm);
  padding: 6px 16px;
  cursor: pointer;
  font-weight: bold;
  font-size: var(--font-size-md);
}

.btnSecondary {
  background: transparent;
  color: var(--text-primary);
  border: 1px solid var(--border-medium);
  border-radius: var(--radius-sm);
  padding: 6px 16px;
  cursor: pointer;
  font-size: var(--font-size-md);
}

.btnSmall {
  background: transparent;
  border: 1px solid var(--border-medium);
  color: var(--text-secondary);
  border-radius: var(--radius-sm);
  /* The small button keeps its old footprint: it sits in dense strips (the
     map's toolbar, the deploy bar) where a pixel more per button is a row
     more of wrapping, and a row is map height. */
  padding: 3px 8px;
  cursor: pointer;
  font-size: var(--font-size-xs);
}

.btnSecondary:hover:not(:disabled),
.btnSmall:hover:not(:disabled) {
  border-color: var(--accent);
  color: var(--text-primary);
}

.btnPrimary:disabled,
.btnSecondary:disabled,
.btnSmall:disabled {
  opacity: 0.45;
  cursor: default;
}

.btnPrimary:focus-visible,
.btnSecondary:focus-visible,
.btnSmall:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}

/* THE ACTOR'S OWN CONTROLS (plan body-map C1): the primary shape in the actor's colour - the trigger. The
   highlight (--accent) stays everyone's; this is "me". Composes, so it never hangs on stylesheet order. */
.btnActor {
  composes: btnPrimary;
  background: var(--actor);
  border-color: var(--actor);
}

.btnActor:focus-visible {
  outline-color: var(--actor);
}

/* A prompt's answers (v8.424.0). The first reaction on offer is the main
   choice, filled; the other is outlined in the same accent. They were a
   blue and a purple slab, each with its own three colours. */
.btnDodge {
  background: var(--accent);
  border: 1px solid var(--accent);
  color: var(--bg-base);
  border-radius: var(--radius-sm);
  padding: 6px 14px;
  cursor: pointer;
  font-weight: bold;
  font-size: var(--font-size-md);
}

.btnParry {
  background: transparent;
  border: 1px solid var(--accent);
  color: var(--text-primary);
  border-radius: var(--radius-sm);
  padding: 6px 14px;
  cursor: pointer;
  font-size: var(--font-size-md);
}

.btnDodge:disabled,
.btnParry:disabled {
  opacity: 0.45;
  cursor: default;
}

.btnDodge:focus-visible,
.btnParry:focus-visible {
  outline: 2px solid var(--text-primary);
  outline-offset: 1px;
}

/* ---- Modal ---- */
.modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modalContent {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  padding: 20px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

/* ---- Forms ---- */
.formGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.label {
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  gap: 2px;
}

.checkRow {
  flex-direction: row;
  align-items: center;
  gap: 6px;
}

.input {
  background: var(--bg-elevated);
  border: 1px solid var(--border-medium);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  padding: 4px 6px;
  font-size: var(--font-size-md);
  outline: none;
}

.input:focus-visible {
  border-color: var(--accent);
}

/* ---- Dropdown ---- */
.dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--bg-card);
  border: 1px solid var(--border-medium);
  border-radius: var(--radius-md);
  min-width: 170px;
  z-index: 100;
  box-shadow: var(--shadow-dropdown);
}

.dropdownItem {
  padding: 6px 12px;
  cursor: pointer;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.dropdownItem:hover {
  background: var(--bg-input);
}

/* ---- Panel header ---- */
.panelHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  font-weight: bold;
  color: var(--accent);
  font-family: var(--font-heading);
  font-size: var(--font-size-md);
  border-bottom: 1px solid var(--border-dim);
  flex-shrink: 0;
}

/* ---- Panel sub-tabs (shared by AttackerPanel + TargetPanel) ---- */
.panelTabBar {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--border-dim);
  flex-shrink: 0;
}

.panelTab {
  flex: 1;
  padding: 5px 8px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-muted);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-family: var(--font-body);
  text-align: center;
  white-space: nowrap;
  transition:
    color 0.15s,
    border-color 0.15s;
}

.panelTab:hover {
  color: var(--text-secondary);
}

.panelTabActive {
  color: var(--text-primary);
  border-bottom-color: var(--accent);
}

/* ---- Overview grid (shared by Overview, Characteristics, Defender, Gear tabs) ---- */
.overviewGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  padding: 8px;
}

.overviewItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 6px;
  background: var(--bg-elevated);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.overviewValue {
  color: var(--accent);
  font-weight: bold;
}

.overviewEmpty {
  padding: 20px;
  text-align: center;
  color: var(--text-faint);
  font-size: var(--font-size-sm);
}

/* ---- Log entry ---- */
.logEntry {
  padding: 6px 10px;
  margin-bottom: 6px;
  background: transparent;
  border-radius: 0;
  font-size: var(--font-size-md);
  line-height: 1.5;
}
